/**
 * Utilities for building breadcrumb links while preserving optional query state.
 */
import { useLocation } from 'react-router-dom';

/**
 * Build a breadcrumb link and optionally preserve existing query string.
 *
 * @param basePath - The path to navigate to
 * @param preserveQuery - Whether to preserve the query string (defaults to true)
 * @param location - Current location from useLocation (pass explicitly to avoid hook dependency)
 * @returns The full href with optional query string
 */
export function buildBreadcrumbHref(
  basePath: string,
  preserveQuery: boolean = true,
  location?: { search: string }
): string {
  if (preserveQuery && location?.search) {
    return `${basePath}${location.search}`;
  }

  return basePath;
}

/**
 * Hook to get the current query parameters as an object.
 * Useful for accessing specific query params like the active tab.
 *
 * @example
 * const params = useQueryParams();
 * const activeTab = params.tab;
 */
export function useQueryParams(): Record<string, string> {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

/**
 * Get a specific query parameter value.
 *
 * @example
 * const activeTab = getQueryParam('tab', location);
 */
export function getQueryParam(
  key: string,
  location: { search: string }
): string | null {
  const params = new URLSearchParams(location.search);
  return params.get(key);
}
